import { useEffect, useState } from "react"
import Input, { clearForm, FormState, required, submitForm, useAreaInput, useTextInput } from "../../../../components/input"
import { api } from "../../../../env"
import MTable, { TABLE_STYLE } from "../../../../components/mtable"
import { TrackStatus } from "../../../../utils/enums"

export default function CreateTrackChapters({track, onTrackUpdate}) {

    const [state, setState] = useState(FormState.UNKNOWN)
    const [displayTrack, setDisplayTrack] = useState(undefined)
    useEffect(() => {
        setDisplayTrack(track)
        if (track) setState(FormState.UNKNOWN)
    }, [track])

    useEffect(() => {
        if (state == FormState.UNKNOWN && track) setState(track.chapters?.length == 0 ? FormState.CREATE : FormState.VIEW)
    }, [state, track])

    const [displayChapter, setDisplayChapter] = useState(undefined)

    const chaptersMapper = {columns: [
        {
            label: "Title",
            value: (item) => item.title
        },
        {
            label: "Activities",
            value: (item) => item?.activities?.length == 0 ? '--' : item.activities.map(it => it.title).join(', ')
        },
        {
            label: "Sparks",
            value: (item) => item?.sparks?.length == 0 ? '--' : item.sparks.map(it => it.displayName).join(', ')
        }
    ]}

    const handleOnCreate = () => {
        setState(FormState.CREATE)
    }

    const handleOnEdit = (chapter) => {
        setState(FormState.UPDATE)
        setDisplayChapter(chapter)
    }

    const handleOnDelete = () => {
        if (track?.status == TrackStatus.PUBLISHED) return
        if (displayChapter.id) api.delete(`chapters/${displayChapter.id}`)
            .then(_ => removeDeletedChapter(displayChapter))
            .catch(_ => {})
        else removeDeletedChapter(displayChapter)
    }
    const removeDeletedChapter = (chapter) => {
        onTrackUpdate({
            ...track,
            chapters: track.chapters.filter(it => it != chapter)
        })
        setDisplayChapter(undefined)
    }

    const handleOnTrackUpdate = (track) => {
        setState(FormState.VIEW)
        setDisplayChapter(undefined)
        onTrackUpdate(track)
    }

    return(<>
        {state == FormState.UNKNOWN && <div className="dashed-card standard-padding">
            <p className="card-title neutral">Please create the <span className="accent">Track</span> first, in order to manage it's <span className="accent">Chapters</span>.</p>
        </div>}
        {state == FormState.VIEW && <>
            <div className="flex space-between gap-xs center v-spacer-xs">
                <p className="section-title-s">Chapters</p>
                {displayTrack?.status == 'Draft' && <button className="solid" onClick={handleOnCreate}>Create Chapter</button>}
            </div>
            <MTable mapper={chaptersMapper} rawData={track?.chapters ?? []} onClick={track.status == TrackStatus.DRAFT ? handleOnEdit : undefined}/>
        </>}
        {(state == FormState.CREATE || state == FormState.UPDATE) && <CreateTrackChapter track={displayTrack} selectedChapter={displayChapter} onTrackUpdate={handleOnTrackUpdate} onDelete={handleOnDelete}/>}
    </>)
}

function CreateTrackChapter({track, selectedChapter, onTrackUpdate, onDelete}) {

    const [state, setState] = useState(FormState.CREATE)
    const [chapter, setChapter] = useState(undefined)
    const [displayTrack, setDisplayTrack] = useState(undefined)
    const [selectedActivity, setSelectedActivity] = useState(undefined)
    const [showTemplatePicker, setShowTemplatePicker] = useState(false)

    useEffect(() => {
        setDisplayTrack(track)
    }, [track])

    useEffect(() => {
        if (selectedChapter) {
            setState(FormState.UPDATE)
            setChapter(selectedChapter)
            createChapterForm.title._setValue(selectedChapter?.title ?? "")
            createChapterForm.theme._setValue(selectedChapter?.theme ?? "")
        }
    }, [selectedChapter])
    
    const createChapterForm = {
        title: useTextInput("Title", "description", [required]),
        theme: useTextInput("Theme", "category", [])
    }
    
    const createActivityForm = {
        title: useTextInput("Title", "label", [required]),
        description: useAreaInput("Description", "description", [], 5)
    }

    const addActivity = () => {
        let activityInfo = submitForm(createActivityForm)
        if (!activityInfo) return
        setChapter(it => {
            return {
                ...it,
                activities: [
                    ...(it?.activities ?? []),
                    // ...(state == FormState.CREATE ? (it?.activities ?? []) : (it?.activities ?? []).map(it => it.id)),
                    activityInfo
                ],
            }
        })
        clearForm(createActivityForm)
    }

    const saveActivity = () => {
        if (!selectedActivity) {
            clearForm(createActivityForm)
            return
        }
        let activityInfo = submitForm(createActivityForm)
        if (!activityInfo) return
        if (selectedActivity?.id && selectedActivity.id != "") {
            api.put(`activity/${selectedActivity.id}`, activityInfo)
                .then(res => {
                    let activity = res.data
                    setSelectedActivity(undefined) 
                    clearForm(createActivityForm)
                    setChapter(it => {
                        const newVal = {
                            ...it,
                            activities: [
                                ...(it.activities.filter(a => a.id != activity.id)),
                                activity
                            ]
                        }
                        newVal.activities.sort((a, b) => a.order - b.order)
                        return newVal                    
                    })
                })
                .catch(err => {})
        }
        else {
            setChapter(it => {
                return {
                    ...it,
                    activities: [
                        ...(it.activities.filter(a => a.title != selectedActivity.title && a.description != selectedActivity.description)),
                        activityInfo
                    ]
                }
            })
            setSelectedActivity(undefined)
            clearForm(createActivityForm)
        }

    }

    const deleteActivity = (activity) => {
        setChapter(it => {
            return {
                ...it,
                activities: it.activities.filter(it => it != activity)
            }
        })
        onActivitySelected(undefined)
    }

    const onActivitySelected = (activity) => {
        setSelectedActivity(activity)
        if (!activity) clearForm(createActivityForm)
        else {
            createActivityForm.title._setValue(activity.title)
            createActivityForm.description._setValue(activity.description)
        }
    }

    const openTemplatePicker = () => {
        setShowTemplatePicker(v => !v)
    }

    const applyTemplate = (template) => {
        createActivityForm.title._setValue(template.name)
        createActivityForm.description._setValue(template.description)
        setShowTemplatePicker(false)
    }

    const activitiesMapper = {columns: [
        {label: "Title", value: (item) => item.title},
        {label: "Description", value: (item) => item.description}
    ]}

    const templatesMapper = {columns: [
        {label: "Name", value: (item) => item.name},
        {label: "Description", value: (item) => item.description}
    ]}

    const addChapter = () => {
        let chapterInfo = submitForm(createChapterForm)
        if(!chapterInfo) return
        let newChapter = {
            ...chapter,
            ...chapterInfo,
            description: "UNUSED",
            position: track?.chapters?.length ?? 0
        }
        console.log(newChapter)
        api.post(`tracks/${track.id}/chapters`, newChapter)
            .then(res => {
                onTrackUpdate(res.data)
                clearForm(createActivityForm)
                clearForm(createChapterForm)
                setChapter(undefined)
            })
            .catch(err => console.log(err))
    }

    const sparksMapper = {columns: [
        {
            label: "Name",
            value: (item) => item.name
        },
        {
            label: "Description",
            value: (item) => item.description,
            width: '2fr'
        }
    ]}

    const handleSparkSelected = (selection) => {
        setChapter(it => {
            return {
                ...it,
                sparkIds: [
                    ...(selection.map(s => s.id))
                ],
            }
        })
    }

    const handleUpdateMeta = () => {
        let chapterInfo = submitForm(createChapterForm)
        if (!chapterInfo) return
        chapterInfo.sparkIds = chapter?.sparkIds
        chapterInfo.activities = chapter?.activities ?? []
        api.put(`chapters/${chapter.id}`, {...chapterInfo, description: "UNUSED"})
            .then(res => {
                let chapter = res.data
                const newTrack = {
                    ...displayTrack,
                    chapters: [
                        ...(displayTrack.chapters.filter(c => c.id != chapter.id)),
                        chapter
                    ]
                }
                newTrack.chapters.sort((a, b) => a.order - b.order)
                onTrackUpdate(newTrack)
                // TODO Toast to success
            })
            .catch(err => {})
    }

    return(<div className="showing">
        <div className="flex space-between gap-xs center v-spacer-xs">
            <p className="section-title-s">Add Chapter</p>
            {state == FormState.CREATE && <button className="text" onClick={() => onTrackUpdate(track)}>Cancel</button>}
            {state == FormState.UPDATE && <div className="flex center gap-xs">
                {track?.status == TrackStatus.DRAFT && <button className="solid-error" onClick={onDelete}>Delete</button>}
                <button className="solid" onClick={handleUpdateMeta}>Save Changes</button>
            </div>}
        </div>
        <div className="card standard-padding-s flex columns gap-xs vi-spacer-xs">
            <p className="card-title">Chapter Details</p>
            <Input state={createChapterForm.title}/>
            <Input state={createChapterForm.theme}/>

            <p className="card-title vi-spacer-l">Activities</p>
            <div className="grid gap-xs" style={{gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))'}}>
                {chapter?.activities?.map((activity, idx) => <div className="dashed-card justify-start gap-s columns padding-s showing clickable" onClick={() => onActivitySelected(activity)}>
                    <p className="card-title">{activity.title}</p>
                    <p className="card-body pre-wrap">{activity.description}</p>
                </div>)}
            </div>
            
            {!showTemplatePicker && <>
                <Input state={createActivityForm.title}/>
                <Input state={createActivityForm.description}/>
            </>}

            {showTemplatePicker && <>
                <div className="flex space-between center gap-xs vi-spacer-s">
                    <p className="card-body primary medium">Pick an Activity</p>
                    <button className="outline v-spacer-xs" onClick={openTemplatePicker}>Close</button>
                </div>
                <MTable path={'activity-templates'} mapper={templatesMapper} onClick={applyTemplate} cardView={true} style={TABLE_STYLE.OUTLINE}/>
            </>}
            <div className="flex justify-end gap-xs">
                {!selectedActivity && !showTemplatePicker && <>
                    <button className="text" onClick={openTemplatePicker}>Pick from template</button>
                    <button className="outline" onClick={addActivity}>Add Activity</button>
                </>}
                {selectedActivity && <>
                    <button className="outline" onClick={() => onActivitySelected(undefined)}>Cancel</button>
                    <button className="solid-error" onClick={() => deleteActivity(selectedActivity)}>Delete</button>
                    <button className="solid" onClick={saveActivity}>Save</button>
                </>}
            </div>

            {track.status == TrackStatus.DRAFT && <>
                <p className="card-title vi-spacer-l">Sparks</p>
                <MTable path={'sparks'} mapper={sparksMapper} selectable={{onChange: handleSparkSelected, multiple: true, cards: true, preselected: (chapter?.sparks ?? []).map(it => { return {id: it.sparkId} } )}} style={TABLE_STYLE.OUTLINE}/>
            </>}

            {state == FormState.CREATE && <>
                <hr className="vi-spacer-l"/>
                <button className="solid large" onClick={addChapter}>Add Chapter</button>
            </>}
        </div>
    </div>)
}